import axios from "axios";

import { baseUrl } from "../store/slices/auth";

export type CareerEventKind = "quick_work" | "partner_vacancy" | "consultation";
export type CareerEventStatus =
  | "scheduled"
  | "active"
  | "completed"
  | "cancelled"
  | "closed";
export type CareerEventVisibility = "draft" | "published" | "archived";

export type CareerUser = {
  _id: string;
  username: string;
  telephone?: string;
  email?: string;
  role?: string;
  programAccess?: string;
  cohortId?: string | null;
};

export type CareerEvent = {
  _id: string;
  calendarSource?: "career";
  kind: CareerEventKind;
  title: string;
  company?: string;
  description?: string;
  requirements?: string;
  responsibilities?: string;
  benefits?: string;
  hasReservation?: boolean;
  isInstantWork?: boolean;
  isDigitalWork?: boolean;
  payout?: string;
  startLabel?: string;
  formatLabel?: string;
  schedule?: string;
  location?: string;
  employerContact?: string;
  tags?: string[];
  startsAt: string;
  endsAt: string;
  status: CareerEventStatus;
  visibility: CareerEventVisibility;
  primaryParticipantId?: CareerUser | string | null;
  participantIds?: Array<CareerUser | string>;
  createdAt?: string;
  updatedAt?: string;
};

export type CohortCalendarEvent = {
  _id: string;
  calendarSource: "cohort";
  kind: "cohort_activity";
  title: string;
  description?: string;
  startsAt: string;
  endsAt: string;
  status: string;
  location?: string;
  cohort?: {
    _id: string;
    name: string;
    code: string;
  } | null;
};

export type CareerEventPayload = {
  kind: CareerEventKind;
  title: string;
  company?: string;
  description?: string;
  requirements?: string;
  responsibilities?: string;
  benefits?: string;
  hasReservation?: boolean;
  isInstantWork?: boolean;
  isDigitalWork?: boolean;
  payout?: string;
  startLabel?: string;
  formatLabel?: string;
  schedule?: string;
  location?: string;
  employerContact?: string;
  tags?: string[];
  startsAt: string;
  endsAt: string;
  status: CareerEventStatus;
  visibility: CareerEventVisibility;
  primaryParticipantId?: string;
  participantIds?: string[];
};

const authHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const careerApi = {
  listUsers: async () => {
    const response = await axios.get<{ users: CareerUser[] }>(
      `${baseUrl}admin/career/users`,
      { headers: authHeaders() }
    );
    return response.data.users;
  },

  listEvents: async (includeCohortEvents = false) => {
    const response = await axios.get<{
      events: CareerEvent[];
      cohortEvents: CohortCalendarEvent[];
    }>(`${baseUrl}admin/career/events`, {
      headers: authHeaders(),
      params: { includeCohortEvents },
    });
    return response.data;
  },

  createEvent: async (payload: CareerEventPayload) => {
    const response = await axios.post<{ event: CareerEvent }>(
      `${baseUrl}admin/career/events`,
      payload,
      { headers: authHeaders() }
    );
    return response.data.event;
  },

  updateEvent: async (id: string, payload: Partial<CareerEventPayload>) => {
    const response = await axios.patch<{ event: CareerEvent }>(
      `${baseUrl}admin/career/events/${id}`,
      payload,
      { headers: authHeaders() }
    );
    return response.data.event;
  },

  deleteEvent: async (id: string) => {
    await axios.delete(`${baseUrl}admin/career/events/${id}`, {
      headers: authHeaders(),
    });
  },

  listPublicJobs: async () => {
    const response = await axios.get<{ jobs: CareerEvent[] }>(
      `${baseUrl}career/jobs`
    );
    return response.data.jobs;
  },
};
